import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Modal,
  Alert,
  Spinner,
  Badge,
  ButtonGroup,
  InputGroup,
} from "react-bootstrap";
import {
  FaExclamationTriangle,
  FaTrashAlt,
  FaEye,
  FaShieldAlt,
  FaChevronRight,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";
import SystemAdminService from "../services/system-admin.service";
import EventBus from "../common/EventBus";

const CLASS_LABELS = {
  movement: "Bewegungsdaten",
  master: "Stamm-/Strukturdaten",
  reference: "Geo-/Referenzdaten",
  config: "Konfiguration / Vorlagen",
  access: "Zugang (geschützt)",
};

const CLASS_ORDER = ["movement", "master", "reference", "config", "access"];

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d).slice(0, 10) : dt.toLocaleDateString("de-DE");
};

// ---------------------------------------------------------------------------
// Datensatzliste EINER Entitaet (fachlich): Suche + Pagination + Einzelauswahl.
// ---------------------------------------------------------------------------
const EntityRecordPicker = ({ entityKey, selectedIds, onToggle, onToggleMany }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    (nextPage, nextSearch) => {
      setLoading(true);
      setError("");
      SystemAdminService.getRecords({
        entity: entityKey,
        page: nextPage,
        pageSize: 25,
        search: nextSearch,
      })
        .then((response) => {
          setRows(response.data.rows || []);
          setTotal(response.data.total || 0);
          setPage(response.data.page || 1);
          setPageSize(response.data.pageSize || 25);
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            err.toString();
          setError(msg);
          if (err.response && err.response.status === 401) EventBus.dispatch("logout");
        })
        .finally(() => setLoading(false));
    },
    [entityKey]
  );

  useEffect(() => {
    load(1, "");
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / (pageSize || 25)));
  const selSet = new Set(selectedIds || []);
  const pageIds = rows.map((r) => r.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selSet.has(id));

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    load(1, searchInput);
  };

  return (
    <div className="border rounded p-2 mb-2 bg-light">
      <Form onSubmit={submitSearch} className="mb-2">
        <InputGroup size="sm">
          <Form.Control
            type="text"
            placeholder="Suchen (Name, Nummer, ...)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button type="submit" variant="outline-secondary">
            <FaSearch />
          </Button>
        </InputGroup>
      </Form>

      {error && (
        <Alert variant="danger" className="py-1 px-2 small mb-2">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" /> Lade Datensätze...
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
            <span>
              {total} Datensätze{search ? ` (gefiltert: "${search}")` : ""}
            </span>
            {rows.length > 0 && (
              <Form.Check
                type="checkbox"
                id={`allpage-${entityKey}`}
                label="Seite auswählen"
                checked={allOnPageSelected}
                onChange={(e) => onToggleMany(entityKey, pageIds, e.target.checked)}
              />
            )}
          </div>
          {rows.length === 0 ? (
            <div className="text-muted small py-1">Keine Datensätze.</div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {rows.map((r) => (
                <Form.Check
                  key={r.id}
                  type="checkbox"
                  id={`rec-${entityKey}-${r.id}`}
                  className="small"
                  checked={selSet.has(r.id)}
                  onChange={() => onToggle(entityKey, r.id)}
                  label={
                    <span>
                      {r.label}
                      {r.date && <span className="text-muted"> · {fmtDate(r.date)}</span>}
                    </span>
                  }
                />
              ))}
            </div>
          )}
          {pageCount > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => load(page - 1, search)}
              >
                Zurück
              </Button>
              <span className="small">
                Seite {page} / {pageCount}
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= pageCount}
                onClick={() => load(page + 1, search)}
              >
                Weiter
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const BoardSystemReset = () => {
  const [entities, setEntities] = useState([]);
  const [meta, setMeta] = useState({
    confirmPhrase: "LÖSCHEN",
    factoryPhrase: "WERKSRESET",
    environment: "",
    productionAllowed: false,
    featureEnabled: false,
  });

  const [mode, setMode] = useState("selected"); // 'selected' | 'factory'
  const [wholeSel, setWholeSel] = useState({}); // entityKey -> bool (alle)
  const [recordSel, setRecordSel] = useState({}); // entityKey -> number[]
  const [excluded, setExcluded] = useState({}); // entityKey -> bool (Werksreset-Ausnahmen)
  const [expanded, setExpanded] = useState({}); // entityKey -> bool
  const [resetAutoIncrement, setResetAutoIncrement] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Bitte warten...");
  const [pageError, setPageError] = useState("");
  const [actionError, setActionError] = useState("");

  const [showWarnModal, setShowWarnModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [phraseInput, setPhraseInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");

  useEffect(() => {
    setLoading(true);
    setLoadingText("Menüs werden geladen...");
    SystemAdminService.getEntities()
      .then((response) => {
        setEntities(response.data.entities || []);
        setMeta({
          confirmPhrase: response.data.confirmPhrase || "LÖSCHEN",
          factoryPhrase: response.data.factoryPhrase || "WERKSRESET",
          environment: response.data.environment || "",
          productionAllowed: !!response.data.productionAllowed,
          featureEnabled: !!response.data.featureEnabled,
        });
      })
      .catch((error) => {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setPageError(msg);
        if (error.response && error.response.status === 401) EventBus.dispatch("logout");
      })
      .finally(() => setLoading(false));
  }, []);

  const selectableEntities = entities.filter((e) => !e.protected);

  const groups = CLASS_ORDER.map((cls) => ({
    cls,
    label: CLASS_LABELS[cls] || cls,
    items: entities.filter((e) => e.className === cls),
  })).filter((g) => g.items.length > 0);

  const buildPayload = () => {
    if (mode === "factory") {
      return {
        factoryReset: true,
        exclude: Object.keys(excluded).filter((k) => excluded[k]),
      };
    }
    const selection = {};
    selectableEntities.forEach((e) => {
      if (wholeSel[e.key]) {
        selection[e.key] = "ALL";
      } else if ((recordSel[e.key] || []).length) {
        selection[e.key] = recordSel[e.key];
      }
    });
    return { factoryReset: false, selection, resetAutoIncrement };
  };

  const hasSelection = () => {
    if (mode === "factory") return true;
    const p = buildPayload();
    return Object.keys(p.selection).length > 0;
  };

  const toggleWhole = (key) => {
    setWholeSel((prev) => ({ ...prev, [key]: !prev[key] }));
    setPreview(null);
  };
  const toggleExcluded = (key) => {
    setExcluded((prev) => ({ ...prev, [key]: !prev[key] }));
    setPreview(null);
  };
  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleRecord = (key, id) => {
    setRecordSel((prev) => {
      const cur = new Set(prev[key] || []);
      if (cur.has(id)) cur.delete(id);
      else cur.add(id);
      return { ...prev, [key]: Array.from(cur) };
    });
    setPreview(null);
  };
  const toggleManyRecords = (key, ids, checked) => {
    setRecordSel((prev) => {
      const cur = new Set(prev[key] || []);
      ids.forEach((id) => (checked ? cur.add(id) : cur.delete(id)));
      return { ...prev, [key]: Array.from(cur) };
    });
    setPreview(null);
  };

  const recordCountFor = (key) => (recordSel[key] || []).length;

  const clearAll = () => {
    setWholeSel({});
    setRecordSel({});
    setExcluded({});
    setPreview(null);
  };

  const runPreview = () => {
    setActionError("");
    setResult(null);
    setPreview(null);
    if (!hasSelection()) {
      setActionError("Bitte mindestens ein Menü oder einzelne Datensätze auswählen.");
      return;
    }
    setLoading(true);
    setLoadingText("Vorschau (inkl. Folgelöschungen) wird berechnet...");
    SystemAdminService.previewWipe(buildPayload())
      .then((response) => setPreview(response.data))
      .catch((error) => {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setActionError(msg);
        if (error.response && error.response.status === 401) EventBus.dispatch("logout");
      })
      .finally(() => setLoading(false));
  };

  const openWarn = () => {
    setActionError("");
    if (!hasSelection()) {
      setActionError("Bitte mindestens ein Menü oder einzelne Datensätze auswählen.");
      return;
    }
    if (!preview) {
      setActionError("Bitte zuerst die Vorschau ausführen.");
      return;
    }
    setShowWarnModal(true);
  };

  const proceedToConfirm = () => {
    setShowWarnModal(false);
    setPhraseInput("");
    setPasswordInput("");
    setShowConfirmModal(true);
  };

  const expectedPhrase = mode === "factory" ? meta.factoryPhrase : meta.confirmPhrase;
  const canExecute =
    phraseInput.trim() === expectedPhrase && passwordInput.length > 0 && !loading;

  const runExecute = () => {
    setActionError("");
    setLoading(true);
    setLoadingText(
      mode === "factory" ? "Werksreset wird ausgeführt..." : "Datenbereinigung läuft..."
    );
    const payload = {
      ...buildPayload(),
      confirmationPhrase: phraseInput.trim(),
      adminPassword: passwordInput,
    };
    SystemAdminService.executeWipe(payload)
      .then((response) => {
        setResult(response.data);
        setPreview(null);
        setShowConfirmModal(false);
        setPhraseInput("");
        setPasswordInput("");
        clearAll();
      })
      .catch((error) => {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setActionError(msg);
        if (error.response && error.response.status === 401) EventBus.dispatch("logout");
      })
      .finally(() => setLoading(false));
  };

  if (pageError) {
    return (
      <Container className="mt-4">
        <h1 className="mb-3">
          <FaShieldAlt className="me-2" />
          Datenbereinigung / Werksreset
        </h1>
        <Alert variant="danger">
          <Alert.Heading>Zugriff nicht möglich</Alert.Heading>
          <p className="mb-0">{pageError}</p>
          <hr />
          <p className="mb-0 small">
            Hinweis: Diese Funktion ist nur für freigeschaltete Super-Administratoren
            verfügbar und muss serverseitig aktiviert sein (Feature-Flag
            <code> FEATURE_SYSTEM_WIPE</code>).
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4 mb-5">
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}

      <h1 className="mb-1">
        <FaShieldAlt className="me-2" />
        Datenbereinigung / Werksreset
      </h1>
      <p className="text-muted">
        Ganze Bereiche leeren oder einzelne Datensätze gezielt löschen. Alle
        zugehörigen Daten (Belege, Transaktionen, Konfigurationen, untergeordnete
        Datensätze) werden automatisch mitgelöscht, damit keine unvollständigen Reste
        verbleiben. Jede Aktion ist unwiderruflich und erfordert eine doppelte
        Bestätigung.
      </p>

      <div className="mb-3">
        <Badge bg={meta.environment === "production" ? "danger" : "secondary"} className="me-2">
          Umgebung: {meta.environment || "unbekannt"}
        </Badge>
        {meta.environment === "production" && !meta.productionAllowed && (
          <Badge bg="warning" text="dark">
            Werksreset in Produktion gesperrt
          </Badge>
        )}
      </div>

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}

      {result && (
        <Alert variant="success">
          <Alert.Heading>{result.message}</Alert.Heading>
          <p className="mb-1">
            Insgesamt gelöschte Datensätze: <strong>{result.totalDeleted}</strong>
          </p>
          {typeof result.filesDeleted === "number" && result.filesTotal > 0 && (
            <p className="mb-1 small">
              Physische Dateien entfernt: <strong>{result.filesDeleted}</strong>
              {result.filesTotal ? ` von ${result.filesTotal}` : ""}
              {result.fileErrors ? ` (${result.fileErrors} Fehler – siehe Audit)` : ""}
            </p>
          )}
          {result.backup && (
            <p className="mb-1 small">
              Vorab-Backup erstellt: <code>{result.backup.file}</code> (
              {result.backup.bytes} Bytes)
            </p>
          )}
          {Array.isArray(result.autoIncrementReset) && result.autoIncrementReset.length > 0 && (
            <p className="mb-1 small">
              AUTO_INCREMENT zurückgesetzt: {result.autoIncrementReset.length} Tabelle(n).
            </p>
          )}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <strong>1. Modus wählen</strong>
        </Card.Header>
        <Card.Body>
          <ButtonGroup className="mb-2 flex-wrap">
            <Button
              variant={mode === "selected" ? "primary" : "outline-primary"}
              onClick={() => {
                setMode("selected");
                setPreview(null);
              }}
            >
              Gezielt löschen
            </Button>
            <Button
              variant={mode === "factory" ? "danger" : "outline-danger"}
              onClick={() => {
                setMode("factory");
                setPreview(null);
              }}
            >
              Kompletter Werksreset
            </Button>
          </ButtonGroup>
          <Form.Check
            type="checkbox"
            id="resetAutoIncrement"
            className="mt-2"
            label="Nach dem Leeren AUTO_INCREMENT (IDs) auf 1 zurücksetzen (nur bei geleerten Tabellen)"
            checked={resetAutoIncrement}
            onChange={(e) => setResetAutoIncrement(e.target.checked)}
          />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
          <strong>
            {mode === "factory"
              ? "2. Ausnahmen festlegen (diese Bereiche bleiben erhalten)"
              : "2. Bereiche und/oder einzelne Datensätze auswählen"}
          </strong>
          {mode === "selected" && (
            <Button size="sm" variant="outline-secondary" onClick={clearAll}>
              Auswahl leeren
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {mode === "factory" ? (
            <>
              <Alert variant="warning" className="py-2">
                Der Werksreset leert <strong>alle</strong> Bereiche
                <em> außer</em> den hier angehakten Ausnahmen. Geo-/Referenzdaten sind
                standardmäßig ausgenommen, können aber durch Entfernen des Hakens
                bewusst mit-gelöscht werden.
              </Alert>
              <Row>
                {groups.map((group) => (
                  <Col md={6} lg={4} key={group.cls} className="mb-3">
                    <div className="fw-bold mb-2 text-uppercase small text-muted">
                      {group.label}
                    </div>
                    {group.items.map((e) => {
                      const isProtected = e.protected;
                      const checked = isProtected
                        ? true
                        : e.key in excluded
                        ? !!excluded[e.key]
                        : !!e.defaultExcluded;
                      return (
                        <Form.Check
                          key={e.key}
                          type="checkbox"
                          id={`excl-${e.key}`}
                          className="mb-1"
                          disabled={isProtected}
                          checked={checked}
                          onChange={() => !isProtected && toggleExcluded(e.key)}
                          label={
                            <>
                              {e.label}
                              {isProtected && (
                                <Badge bg="secondary" className="ms-2">
                                  geschützt
                                </Badge>
                              )}
                              {e.defaultExcluded && !isProtected && (
                                <Badge bg="info" className="ms-2">
                                  Standard-Ausnahme
                                </Badge>
                              )}
                            </>
                          }
                        />
                      );
                    })}
                  </Col>
                ))}
              </Row>
            </>
          ) : (
            <Row>
              {groups.map((group) => (
                <Col lg={6} key={group.cls} className="mb-3">
                  <div className="fw-bold mb-2 text-uppercase small text-muted">
                    {group.label}
                  </div>
                  {group.items.map((e) => {
                    if (e.protected) {
                      return (
                        <div key={e.key} className="mb-2 text-muted">
                          {e.label}{" "}
                          <Badge bg="secondary" className="ms-1">
                            geschützt
                          </Badge>
                        </div>
                      );
                    }
                    const whole = !!wholeSel[e.key];
                    const isExpanded = !!expanded[e.key];
                    const cnt = recordCountFor(e.key);
                    return (
                      <div key={e.key} className="mb-2 border-bottom pb-2">
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            id={`whole-${e.key}`}
                            className="me-2"
                            checked={whole}
                            onChange={() => toggleWhole(e.key)}
                            label={
                              <>
                                <strong>{e.label}</strong>
                                <span className="text-muted"> — alle</span>
                                {e.defaultExcluded && (
                                  <Badge bg="info" className="ms-2">
                                    Referenzdaten
                                  </Badge>
                                )}
                                {cnt > 0 && !whole && (
                                  <Badge bg="primary" className="ms-2">
                                    {cnt} ausgewählt
                                  </Badge>
                                )}
                              </>
                            }
                          />
                          <Button
                            variant="link"
                            size="sm"
                            className="ms-auto p-0 text-decoration-none"
                            onClick={() => toggleExpand(e.key)}
                            disabled={whole}
                            title="Einzelne Datensätze anzeigen"
                          >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />} Datensätze
                          </Button>
                        </div>
                        {isExpanded && !whole && (
                          <div className="mt-2">
                            <EntityRecordPicker
                              entityKey={e.key}
                              selectedIds={recordSel[e.key] || []}
                              onToggle={toggleRecord}
                              onToggleMany={toggleManyRecords}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <strong>3. Vorschau & Ausführung</strong>
        </Card.Header>
        <Card.Body>
          <Button variant="info" className="me-2 mb-2" onClick={runPreview}>
            <FaEye className="me-1" /> Vorschau (inkl. Folgelöschungen)
          </Button>
          <Button variant="danger" className="mb-2" onClick={openWarn} disabled={!preview}>
            <FaTrashAlt className="me-1" />
            {mode === "factory" ? "Werksreset starten" : "Auswahl löschen"}
          </Button>

          {preview && (
            <div className="mt-3">
              <Alert variant="warning">
                <FaExclamationTriangle className="me-2" />
                Es werden insgesamt <strong>{preview.grandTotal}</strong> Datensätze gelöscht
                (inklusive aller automatisch mitgelöschten, zugehörigen Daten).
                {preview.filesOnDisk > 0 && (
                  <div className="mt-1">
                    Zusätzlich werden <strong>{preview.filesOnDisk}</strong> physische Datei(en)
                    (z. B. Rechnungs-PDFs) vom Server entfernt.
                  </div>
                )}
                {!preview.environmentAllowsExecution && (
                  <div className="mt-2 text-danger">
                    Achtung: Ausführung ist in dieser Umgebung derzeit gesperrt (
                    {preview.environmentReason}).
                  </div>
                )}
              </Alert>

              {(preview.perEntity || []).length > 0 && (
                <Table striped bordered hover responsive size="sm" className="mb-2">
                  <thead>
                    <tr>
                      <th>Ausgewählter Bereich</th>
                      <th className="text-end">Direkt gewählte Datensätze</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.perEntity.map((pe) => (
                      <tr key={pe.key}>
                        <td>{pe.label}</td>
                        <td className="text-end">{pe.selected}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => setShowDetails((s) => !s)}
              >
                {showDetails ? <FaChevronDown /> : <FaChevronRight />} Technische Details
                (betroffene Tabellen)
              </Button>
              {showDetails && (
                <Table bordered responsive size="sm" className="mt-2">
                  <thead>
                    <tr>
                      <th>Tabelle</th>
                      <th className="text-end">Datensätze</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.perTable || []).map((pt) => (
                      <tr key={pt.table}>
                        <td>
                          <code>{pt.table}</code>
                        </td>
                        <td className="text-end">{pt.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Stufe 1: Warn-Modal */}
      <Modal show={showWarnModal} onHide={() => setShowWarnModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FaExclamationTriangle className="me-2" />
            Unwiderrufliche Aktion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sie sind dabei,{" "}
            <strong>
              {mode === "factory" ? "einen kompletten Werksreset" : "die getroffene Auswahl"}
            </strong>{" "}
            unwiderruflich zu löschen. Alle zugehörigen Daten werden automatisch
            mitgelöscht.
          </p>
          {preview && (
            <>
              <p className="mb-1">
                Betroffene Datensätze insgesamt: <strong>{preview.grandTotal}</strong>
              </p>
              {(preview.perEntity || []).length > 0 && (
                <ul className="small mb-0">
                  {preview.perEntity.map((pe) => (
                    <li key={pe.key}>
                      {pe.label}: {pe.selected} direkt gewählt
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <Alert variant="danger" className="mb-0 py-2 mt-2">
            Vor der Löschung wird automatisch ein Datenbank-Backup erstellt. Der Vorgang
            kann dennoch nicht rückgängig gemacht werden.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWarnModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={proceedToConfirm}>
            Weiter zur Bestätigung
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stufe 2: Phrase + Passwort */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Endgültige Bestätigung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Tippen Sie zur Bestätigung exakt: <code>{expectedPhrase}</code>
            </Form.Label>
            <Form.Control
              type="text"
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              placeholder={expectedPhrase}
              autoComplete="off"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Ihr Administrator-Passwort</Form.Label>
            <Form.Control
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Passwort"
              autoComplete="current-password"
            />
          </Form.Group>
          {actionError && (
            <Alert variant="danger" className="py-2 mb-0">
              {actionError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" disabled={!canExecute} onClick={runExecute}>
            <FaTrashAlt className="me-1" />
            Jetzt unwiderruflich {mode === "factory" ? "zurücksetzen" : "löschen"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BoardSystemReset;
